const SLACK_ANDROID_PACKAGE = 'com.Slack';

export function isAndroidUserAgent(userAgent: string): boolean {
  return /Android/i.test(userAgent);
}

/** Permalink `p` segment digits → Slack `ts` (dot before last 6 digits). */
export function permalinkDigitsToSlackTs(digits: string): string | null {
  if (!/^\d+$/.test(digits) || digits.length <= 6) return null;
  return `${digits.slice(0, -6)}.${digits.slice(-6)}`;
}

export interface ParsedArchivePermalink {
  readonly channelId: string;
  readonly messageTs: string;
  readonly threadTs: string | null;
}

export function parseArchivePermalink(url: URL): ParsedArchivePermalink | null {
  if (!url.hostname.endsWith('.slack.com')) return null;

  const pathMatch = url.pathname.match(/^\/archives\/([^/]+)\/p(\d+)$/);
  if (!pathMatch) return null;

  const channelId = pathMatch[1];
  const messageTs = permalinkDigitsToSlackTs(pathMatch[2]);
  if (!channelId || !messageTs) return null;

  const threadTsRaw = url.searchParams.get('thread_ts');
  const threadTs = threadTsRaw && threadTsRaw.length > 0 ? threadTsRaw : null;

  return { channelId, messageTs, threadTs };
}

/** Chrome intent handing the unchanged https permalink to the Slack app; no browser fallback. */
export function buildSlackHttpsIntent(permalink: URL): string {
  return `intent://${permalink.host}${permalink.pathname}${permalink.search}#Intent;package=${SLACK_ANDROID_PACKAGE};scheme=https;end`;
}

export interface SlackSourceLinks {
  readonly primaryHref: string;
  readonly primaryOpensNativeApp: boolean;
  readonly copyHref: string;
  readonly showThreadHint: boolean;
}

export function getSlackSourceLinks(
  permalink: string,
  userAgent: string,
): SlackSourceLinks | null {
  let url: URL;
  try {
    url = new URL(permalink);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:') return null;

  const parsed = parseArchivePermalink(url);
  const android = isAndroidUserAgent(userAgent);

  if (android && parsed) {
    return {
      primaryHref: buildSlackHttpsIntent(url),
      primaryOpensNativeApp: true,
      copyHref: permalink,
      showThreadHint: parsed.threadTs !== null,
    };
  }

  return {
    primaryHref: permalink,
    primaryOpensNativeApp: false,
    copyHref: permalink,
    showThreadHint: false,
  };
}
